import {useCallback, useEffect, useState} from 'react';
import {fetchRequirementCollectionWork} from './requirementCollectionApi';

/**
 * Load collection tasks + nested materials for an ICR or EEI.
 *
 * @param {'icr'|'eei'} entityType
 * @param {string|null|undefined} entityId
 * @param {boolean} [enabled=true]
 */
export function useRequirementCollectionWork(entityType, entityId, enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        if (!entityId || !enabled) {
            setData(null);
            setError(null);
            return null;
        }
        try {
            const next = await fetchRequirementCollectionWork(entityType, entityId);
            setData(next);
            setError(null);
            return next;
        } catch (e) {
            setError(e?.message || 'Failed to load collection work');
            return null;
        }
    }, [entityType, entityId, enabled]);

    useEffect(() => {
        if (!entityId || !enabled) {
            setData(null);
            setLoading(false);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        reload().finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [entityId, enabled, reload]);

    const tasks = Array.isArray(data?.tasks) ? data.tasks : [];
    const totalTasks = data?.totalTasks ?? tasks.length;
    const totalMaterials = data?.totalMaterials
        ?? tasks.reduce((sum, t) => sum + (t.materialCount ?? (t.materials || []).length), 0);

    return {
        data,
        tasks,
        totalTasks,
        totalMaterials,
        loading,
        error,
        reload,
    };
}
